<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $tc('messages.fouls', 2),
                        href: meta.links.index,
                    },
                    {
                        name: group.name,
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div>
                        <BadgePill>
                            <a href="#stats">{{ $t('messages.graphs') }}</a>
                        </BadgePill>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <div class="w-full flex justify-end space-x-2">
            <AppButton element="InertiaLink" :href="meta.links.create">
                {{ $t('messages.issue_foul') }}
            </AppButton>
        </div>

        <form :action="meta.links.show" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.subject', 1)" for="school_year_id" />
                        <FormSelect
                            id="subject_id"
                            v-model="form.subject_id"
                            :options="options.subjects"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.subject_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.trimester')" for="trimester" />
                        <FormSelect
                            required
                            id="trimester"
                            v-model="form.trimester"
                            :options="options.trimesters"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.trimester" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2" v-show="options.school_years.length > 1">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.school_year')" for="school_year_id" />
                        <FormSelect
                            required
                            id="school_year_id"
                            v-model="form.school_year_id"
                            :options="options.school_years"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.school_year_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.homework') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.material') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.behaviour') }} <br />
                                    {{ $t('messages.severity_log') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.behaviour') }} <br />
                                    {{ $t('messages.severity_mild') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.behaviour') }} <br />
                                    {{ $t('messages.severity_serious') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.behaviour') }} <br />
                                    {{ $t('messages.severity_very_serious') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(student, index) in students"
                                :key="student.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <span>
                                        <InertiaLink :href="student.links.foul_summary">
                                            {{ student.full_name }}
                                        </InertiaLink>
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <span
                                        :class="{
                                            'text-gray-300': student.foulsSummary.homework === 0,
                                        }"
                                    >
                                        {{ student.foulsSummary.homework }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <span
                                        :class="{
                                            'text-gray-300': student.foulsSummary.material === 0,
                                        }"
                                    >
                                        {{ student.foulsSummary.material }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <span
                                        :class="{
                                            'text-gray-300': student.foulsSummary.behaviour_log === 0,
                                        }"
                                    >
                                        {{ student.foulsSummary.behaviour_log }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <span
                                        :class="{
                                            'text-gray-300': student.foulsSummary.behaviour_mild === 0,
                                        }"
                                    >
                                        {{ student.foulsSummary.behaviour_mild }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <span
                                        :class="{
                                            'text-gray-300': student.foulsSummary.behaviour_serious === 0,
                                        }"
                                    >
                                        {{ student.foulsSummary.behaviour_serious }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <span
                                        :class="{
                                            'text-gray-300': student.foulsSummary.behaviour_very_serious === 0,
                                        }"
                                    >
                                        {{ student.foulsSummary.behaviour_very_serious }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.homework') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.material') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.behaviour') }} <br />
                                    {{ $t('messages.severity_log') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.behaviour') }} <br />
                                    {{ $t('messages.severity_mild') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.behaviour') }} <br />
                                    {{ $t('messages.severity_serious') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.behaviour') }} <br />
                                    {{ $t('messages.severity_very_serious') }}
                                </th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>

        <div id="stats" class="w-full mt-5">
            <h5>{{ $t('messages.behaviour') }}</h5>

            <div id="chart">
                <apexchart type="bar" height="350" :options="chart" :series="chart.series" />
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import InertiaLinkButton from '@/components/InertiaLinkButton.vue';
import BadgePill from '@/components/BadgePill.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import AppButton from '../../../../components/AppButton.vue';

export default {
    components: {
        AppButton,
        SubmitFormButton,
        PanelPage,
        FormLabel,
        FormError,
        FormSelect,
        InertiaLinkButton,
        BadgePill,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'options', 'group', 'filters', 'students', 'chartData'],
    data() {
        return {
            form: this.$inertia.form({
                subject_id: this.filters.subject_id,
                school_year_id: this.filters.school_year_id,
                trimester: this.filters.trimester,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.show);
        },
    },
    computed: {
        chart() {
            return this.chartData;
        },
    },
};
</script>
